/**
 * ReportProblemButton (issue #1268): the always-visible "Report a problem"
 * button in the V2 header. Opens a dialog where any user describes a problem
 * and attaches screenshots (drop, select, or paste); submitting creates a
 * GitHub issue via /v4/bug-reports. Context (page, browser, identity) is
 * captured by the server — the client only sends what the user typed.
 */
import { useState, type ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag, X } from 'lucide-react';
import type { Accept } from 'react-dropzone';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Dropzone } from '@/components/ui/dropzone';
import { Textarea } from '@/components/ui/textarea';

const MIN_DESCRIPTION_LENGTH = 10;
const MAX_DESCRIPTION_LENGTH = 4000;
const MAX_FILES = 5;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ACCEPTED_TYPES: Accept = {
  'image/png': [],
  'image/jpeg': [],
  'image/gif': [],
  'image/webp': [],
  'text/plain': [],
  'application/pdf': [],
};

interface BugReportResponse {
  issueNumber: number;
  issueUrl: string;
  attachments: Array<{ name: string; mode: string; url?: string }>;
}

export function ReportProblemButton() {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = () => {
    setDescription('');
    setFiles([]);
    setSubmitting(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    setOpen(next);
  };

  const addFiles = (incoming: File[]) => {
    const accepted: File[] = [];
    for (const file of incoming) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t('reportProblem.fileTooLarge', { name: file.name, defaultValue: '{{name}} exceeds the 10 MB limit.' }));
        continue;
      }
      if (!(file.type in ACCEPTED_TYPES)) {
        toast.error(
          t('reportProblem.fileTypeRejected', {
            name: file.name,
            defaultValue: '{{name}} is not an accepted file type (image, PDF or text).',
          }),
        );
        continue;
      }
      accepted.push(file);
    }
    if (!accepted.length) return;
    const room = MAX_FILES - files.length;
    if (accepted.length > room) {
      toast.error(t('reportProblem.tooManyFiles', 'Maximum of 5 attachments.'));
    }
    if (room <= 0) return;
    setFiles((prev) => [...prev, ...accepted.slice(0, room)]);
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = Array.from(event.clipboardData.files);
    if (pasted.length) {
      event.preventDefault();
      addFiles(pasted);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.set('description', description);
      formData.set('pageUrl', window.location.pathname + window.location.search);
      formData.set('userAgent', navigator.userAgent);
      for (const file of files) formData.append('files', file);

      const result = await api.upload<BugReportResponse>('/v4/bug-reports', formData);
      toast.success(t('reportProblem.successTitle', 'Report sent'), {
        description: t('reportProblem.successDescription', {
          number: result.issueNumber,
          defaultValue: 'Issue #{{number}} created.',
        }),
        action: {
          label: t('reportProblem.viewIssue', 'View issue'),
          onClick: () => window.open(result.issueUrl, '_blank', 'noopener,noreferrer'),
        },
      });
      handleOpenChange(false);
    } catch (error) {
      toast.error(t('reportProblem.errorTitle', 'Report could not be sent'), {
        description: error instanceof Error ? error.message : undefined,
      });
      setSubmitting(false);
    }
  };

  const canSubmit = description.trim().length >= MIN_DESCRIPTION_LENGTH && !submitting;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex items-center gap-1.5 rounded px-2 py-1 text-sm text-atek-text-muted transition-colors hover:bg-white/5 hover:text-white"
        title={t('reportProblem.button', 'Report a problem')}
      >
        <Flag className="h-4 w-4" />
        <span className="hidden lg:inline">{t('reportProblem.button', 'Report a problem')}</span>
      </button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('reportProblem.title', 'Report a problem')}</DialogTitle>
            <DialogDescription>
              {t(
                'reportProblem.description',
                'Describe what went wrong — screenshots help us fix it faster.',
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label htmlFor="bug-report-description" className="text-sm font-medium">
                {t('reportProblem.descriptionLabel', 'What happened?')}
              </label>
              <Textarea
                id="bug-report-description"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
                onPaste={handlePaste}
                maxLength={MAX_DESCRIPTION_LENGTH}
                placeholder={t(
                  'reportProblem.descriptionPlaceholder',
                  'Describe the problem — which page, what you did, what you expected (minimum 10 characters).',
                )}
                rows={5}
                disabled={submitting}
              />
              <p className="text-xs text-muted-foreground">
                {t('reportProblem.pasteHint', 'You can paste a screenshot directly here (Ctrl+V).')}
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <span className="text-sm font-medium">{t('reportProblem.attachmentsLabel', 'Attachments')}</span>
              <Dropzone
                onFiles={addFiles}
                accept={ACCEPTED_TYPES}
                multiple
                disabled={submitting || files.length >= MAX_FILES}
                label={t('reportProblem.dropzoneLabel', 'Drop screenshots here, or click to select')}
              />
              {files.length > 0 && (
                <ul data-testid="attachment-list" className="flex flex-col gap-1.5">
                  {files.map((file) => (
                    <li
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="flex items-center justify-between rounded border px-2.5 py-1.5 text-sm"
                    >
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setFiles((prev) => prev.filter((f) => f !== file))}
                        className="ml-2 shrink-0 text-muted-foreground hover:text-foreground"
                        aria-label={t('reportProblem.remove', { name: file.name, defaultValue: 'Remove {{name}}' })}
                        disabled={submitting}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
              {t('reportProblem.cancel', 'Cancel')}
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={!canSubmit}>
              {submitting
                ? t('reportProblem.submitting', 'Submitting…')
                : t('reportProblem.submit', 'Submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
